import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { PackagePlus, Trash2, Star, RefreshCw, Package, Lock, LogOut, UploadCloud, X, Pencil, MessageSquare, Download } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import { useStore } from "../context/StoreContext";
import {
  fetchProducts, createProduct, updateProduct, deleteProduct, adminLogin, uploadImage,
  getAdminToken, setAdminToken, clearAdminToken, resolveImageUrl, fetchEnquiries,
  deleteEnquiry, bulkDeleteEnquiries,
} from "../api";
import { LOGO_URL } from "../mock";

const NAVY = "#1B1F5C";

const CATEGORIES = [
  { value: "toys-games", label: "Toys & Games" },
  { value: "learning-education", label: "Learning & Education" },
  { value: "gifts", label: "Gifts" },
];
const AGES = ["0 - 1 Years", "1 - 3 Years", "3 - 6 Years", "6 - 10 Years", "10+ Years"];
const GENDERS = ["", "Boys", "Girls", "Unisex"];
const OCCASIONS = ["", "Birthday", "Festival", "Return Gifts", "Special Occasions"];

const EMPTY_FORM = {
  name: "", category: "toys-games", price: "", mrp: "", image: "", brand: "",
  age: "3 - 6 Years", rating: "4.5", reviews: "0", sku: "", players: "", ageGroup: "",
  description: "", howToPlay: "", gender: "", occasion: "", topPick: false, newArrival: false, inStock: true,
};

const inputCls = "w-full h-10 px-3.5 rounded-lg border border-gray-200 text-[13px] font-semibold outline-none focus:border-[#35C3C1] focus:ring-2 focus:ring-[#35C3C1]/20 bg-white";
const labelCls = "block text-[11.5px] font-extrabold mb-1.5 text-left";

/* ---------- Login screen ---------- */
const AdminLogin = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return toast.error("Please enter the admin password");
    setLoading(true);
    try {
      const data = await adminLogin(password);
      setAdminToken(data.token);
      toast.success("Welcome back, Admin!");
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center py-20 px-4">
      <form onSubmit={handleLogin} className="bg-white rounded-3xl border border-gray-100 shadow-xl p-8 w-full max-w-sm text-center">
        <img src={LOGO_URL} alt="Adhira & Appa Toys" className="h-20 w-auto mx-auto" />
        <span className="mt-5 inline-flex h-12 w-12 rounded-full items-center justify-center" style={{ backgroundColor: NAVY }}>
          <Lock size={20} className="text-[#FCD44E]" />
        </span>
        <h1 className="mt-3 font-display font-extrabold text-xl" style={{ color: NAVY }}>Admin Login</h1>
        <p className="mt-1 text-[12.5px] font-semibold text-gray-500">Enter your password to manage products</p>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Admin password"
          className={`${inputCls} mt-5 h-11 text-center`}
          autoFocus
        />
        <button
          type="submit"
          disabled={loading}
          className="mt-4 w-full h-11 rounded-full text-white text-[12.5px] font-extrabold tracking-widest shadow-md hover:shadow-lg transition-colors disabled:opacity-60"
          style={{ backgroundColor: NAVY }}
        >
          {loading ? "SIGNING IN..." : "LOGIN"}
        </button>
        <Link to="/" className="mt-4 inline-block text-[12px] font-extrabold text-[#35C3C1] hover:underline">← Back to store</Link>
      </form>
    </div>
  );
};

/* ---------- Admin dashboard ---------- */
const Admin = () => {
  const { wishlist, cart } = useStore();
  const [authed, setAuthed] = useState(() => !!getAdminToken());
  const [form, setForm] = useState(EMPTY_FORM);
  const [products, setProducts] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [enqFilter, setEnqFilter] = useState("");
  const [selectedEnq, setSelectedEnq] = useState([]);
  const fileRef = useRef(null);
  const formRef = useRef(null);

  const handleAuthError = (err) => {
    if (err?.response?.status === 401) {
      clearAdminToken();
      setAuthed(false);
      toast.error("Session expired. Please login again.");
      return true;
    }
    return false;
  };

  const load = useCallback(() => {
    setLoading(true);
    fetchProducts(filterCat ? { category: filterCat } : {})
      .then(setProducts)
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoading(false));
  }, [filterCat]);

  useEffect(() => {
    if (authed) load();
  }, [load, authed]);

  useEffect(() => {
    if (authed) fetchEnquiries().then(setEnquiries).catch(() => {});
  }, [authed]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be smaller than 5 MB");
    setUploading(true);
    try {
      const data = await uploadImage(file);
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Image uploaded!");
    } catch (err) {
      if (!handleAuthError(err)) toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      category: p.category || "toys-games",
      price: p.price != null ? String(p.price) : "",
      mrp: p.mrp != null ? String(p.mrp) : "",
      image: p.image || "",
      brand: p.brand || "",
      age: p.age || "3 - 6 Years",
      rating: p.rating != null ? String(p.rating) : "4.5",
      reviews: p.reviews != null ? String(p.reviews) : "0",
      sku: p.sku || "",
      players: p.players || "",
      ageGroup: p.ageGroup || "",
      description: p.description || "",
      howToPlay: (p.howToPlay || []).join("\n"),
      gender: p.gender || "",
      occasion: p.occasion || "",
      topPick: !!p.topPick,
      newArrival: !!p.newArrival,
      inStock: p.inStock !== false,
    });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const toggleStock = async (p) => {
    try {
      const updated = await updateProduct(p.id, { inStock: p.inStock === false });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? updated : x)));
      toast.success(updated.inStock ? `“${p.name}” marked In Stock` : `“${p.name}” marked Out of Stock`);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Failed to update stock");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Product name is required");
    if (!form.price || Number(form.price) <= 0) return toast.error("Enter a valid price");
    if (!form.image.trim()) return toast.error("Upload an image or paste an image URL");

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        price: Number(form.price),
        mrp: form.mrp ? Number(form.mrp) : null,
        image: form.image.trim() || null,
        brand: form.brand.trim() || null,
        age: form.age,
        rating: Number(form.rating) || 4.5,
        reviews: Number(form.reviews) || 0,
        sku: form.sku.trim() || null,
        players: form.players.trim() || null,
        ageGroup: form.ageGroup.trim() || null,
        description: form.description.trim() || null,
        howToPlay: form.howToPlay.trim() ? form.howToPlay.split("\n").map((s) => s.trim()).filter(Boolean) : null,
        gender: form.category === "gifts" && form.gender ? form.gender : null,
        occasion: form.category === "gifts" && form.occasion ? form.occasion : null,
        topPick: form.topPick,
        newArrival: form.newArrival,
        inStock: form.inStock,
      };
      if (editingId) {
        const updated = await updateProduct(editingId, payload);
        toast.success(`“${updated.name}” updated`);
        setEditingId(null);
      } else {
        const created = await createProduct(payload);
        toast.success(`“${created.name}” added to ${CATEGORIES.find((c) => c.value === created.category)?.label}`);
      }
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      if (!handleAuthError(err)) toast.error(err?.response?.data?.detail || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete “${p.name}”?`)) return;
    try {
      await deleteProduct(p.id);
      toast.success("Product deleted");
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Failed to delete product");
    }
  };

  const filteredEnquiries = enquiries.filter((q) => !enqFilter || (q.type || "general") === enqFilter);
  const typeLabel = (t) => (t === "franchise" ? "Franchise" : t === "newsletter" ? "Newsletter" : "General");

  const handleDeleteEnquiry = async (q) => {
    if (!window.confirm(`Delete enquiry from “${q.name}”?`)) return;
    try {
      await deleteEnquiry(q.id);
      setEnquiries((prev) => prev.filter((x) => x.id !== q.id));
      setSelectedEnq((prev) => prev.filter((id) => id !== q.id));
      toast.success("Enquiry deleted");
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Failed to delete enquiry");
    }
  };

  const handleBulkDeleteEnquiries = async () => {
    if (selectedEnq.length === 0) return;
    if (!window.confirm(`Delete ${selectedEnq.length} selected enquir${selectedEnq.length === 1 ? "y" : "ies"}?`)) return;
    try {
      const res = await bulkDeleteEnquiries(selectedEnq);
      setEnquiries((prev) => prev.filter((x) => !selectedEnq.includes(x.id)));
      setSelectedEnq([]);
      toast.success(`${res.deleted} enquir${res.deleted === 1 ? "y" : "ies"} deleted`);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Failed to delete enquiries");
    }
  };

  const toggleEnqSelect = (id) => {
    setSelectedEnq((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const exportEnquiriesCSV = (list, label) => {
    if (list.length === 0) return toast.error("No enquiries to export");
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Name", "Phone", "Email", "City", "Type", "Message", "Date"];
    const rows = list.map((q) => [
      q.name, q.phone, q.email || "", q.city || "", typeLabel(q.type),
      q.message || "", q.createdAt ? new Date(q.createdAt).toLocaleString("en-IN") : "",
    ].map(esc).join(","));
    const csv = [header.map(esc).join(","), ...rows].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `enquiries-${label}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${list.length} enquir${list.length === 1 ? "y" : "ies"}`);
  };

  const handleLogout = () => {
    clearAdminToken();
    setAuthed(false);
    toast("Logged out");
  };

  if (!authed) {
    return (
      <div className="bg-[#F7F7FA] min-h-screen">
        <Header cartCount={cart.length} wishlistCount={wishlist.length} />
        <AdminLogin onSuccess={() => setAuthed(true)} />
      </div>
    );
  }

  return (
    <div className="bg-[#F7F7FA] min-h-screen">
      <Header cartCount={cart.length} wishlistCount={wishlist.length} />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-left">
            <h1 className="font-display font-extrabold text-2xl md:text-3xl" style={{ color: NAVY }}>Product Manager</h1>
            <p className="text-[13px] font-semibold text-gray-500 mt-1">Add new toys to the store – they appear instantly on their category pages</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-[12px] font-extrabold text-[#35C3C1] hover:underline">← Back to store</Link>
            <button onClick={handleLogout} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-gray-200 bg-white text-[11.5px] font-extrabold hover:border-[#F0426A] hover:text-[#F0426A] transition-colors" style={{ color: NAVY }}>
              <LogOut size={13} /> LOGOUT
            </button>
          </div>
        </div>

        <div className="mt-6 grid lg:grid-cols-[1.1fr_1.4fr] gap-6 items-start">
          {/* Add / Edit product form */}
          <form ref={formRef} onSubmit={handleSubmit} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-7 scroll-mt-24">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2.5">
                <span className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: NAVY }}>
                  {editingId ? <Pencil size={16} className="text-[#FCD44E]" /> : <PackagePlus size={18} className="text-[#FCD44E]" />}
                </span>
                <h2 className="font-display font-extrabold text-lg text-left" style={{ color: NAVY }}>
                  {editingId ? "Edit Product" : "Add New Product"}
                </h2>
              </div>
              {editingId && (
                <button type="button" onClick={cancelEdit} className="text-[11.5px] font-extrabold text-gray-400 hover:text-[#F0426A] transition-colors">
                  CANCEL EDIT
                </button>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={labelCls} style={{ color: NAVY }}>Product Name *</label>
                <input className={inputCls} value={form.name} onChange={set("name")} placeholder="e.g., Guess Who? – The Original Guessing Game" />
              </div>
              <div>
                <label className={labelCls} style={{ color: NAVY }}>Category *</label>
                <select className={inputCls} value={form.category} onChange={set("category")}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} style={{ color: NAVY }}>Age Range *</label>
                <select className={inputCls} value={form.age} onChange={set("age")}>
                  {AGES.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} style={{ color: NAVY }}>Selling Price (₹) *</label>
                <input type="number" className={inputCls} value={form.price} onChange={set("price")} placeholder="999" />
              </div>
              <div>
                <label className={labelCls} style={{ color: NAVY }}>MRP (₹)</label>
                <input type="number" className={inputCls} value={form.mrp} onChange={set("mrp")} placeholder="1199 (discount auto-calculated)" />
              </div>

              {/* Image upload */}
              <div className="sm:col-span-2">
                <label className={labelCls} style={{ color: NAVY }}>Product Image *</label>
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="flex-1 h-24 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#35C3C1] transition-colors flex flex-col items-center justify-center gap-1.5 disabled:opacity-60"
                  >
                    <UploadCloud size={22} className="text-[#35C3C1]" />
                    <span className="text-[12px] font-extrabold" style={{ color: NAVY }}>
                      {uploading ? "Uploading..." : "Click to upload image"}
                    </span>
                    <span className="text-[10.5px] font-semibold text-gray-400">PNG, JPG, WEBP · max 5 MB</span>
                  </button>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleFileSelect} />
                  {form.image && (
                    <div className="relative h-24 w-24 rounded-xl bg-[#F7F7FA] border border-gray-200 overflow-hidden shrink-0">
                      <img src={resolveImageUrl(form.image)} alt="Preview" className="h-full w-full object-contain" />
                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, image: "" }))}
                        className="absolute top-1 right-1 h-5 w-5 rounded-full bg-white shadow flex items-center justify-center text-gray-500 hover:text-[#F0426A]"
                        aria-label="Remove image"
                      >
                        <X size={11} />
                      </button>
                    </div>
                  )}
                </div>
                <input className={`${inputCls} mt-2`} value={form.image} onChange={set("image")} placeholder="...or paste an image URL here" />
              </div>

              <div>
                <label className={labelCls} style={{ color: NAVY }}>Brand</label>
                <input className={inputCls} value={form.brand} onChange={set("brand")} placeholder="Hasbro" />
              </div>
              <div>
                <label className={labelCls} style={{ color: NAVY }}>SKU</label>
                <input className={inputCls} value={form.sku} onChange={set("sku")} placeholder="E8263" />
              </div>
              <div>
                <label className={labelCls} style={{ color: NAVY }}>Rating (0-5)</label>
                <input type="number" step="0.1" min="0" max="5" className={inputCls} value={form.rating} onChange={set("rating")} />
              </div>
              <div>
                <label className={labelCls} style={{ color: NAVY }}>Reviews Count</label>
                <input type="number" className={inputCls} value={form.reviews} onChange={set("reviews")} />
              </div>
              <div>
                <label className={labelCls} style={{ color: NAVY }}>Players</label>
                <input className={inputCls} value={form.players} onChange={set("players")} placeholder="2 Players" />
              </div>
              <div>
                <label className={labelCls} style={{ color: NAVY }}>Age Group Label</label>
                <input className={inputCls} value={form.ageGroup} onChange={set("ageGroup")} placeholder="6+" />
              </div>
              {form.category === "gifts" && (
                <>
                  <div>
                    <label className={labelCls} style={{ color: NAVY }}>Gender</label>
                    <select className={inputCls} value={form.gender} onChange={set("gender")}>
                      {GENDERS.map((g) => <option key={g} value={g}>{g || "—"}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} style={{ color: NAVY }}>Occasion</label>
                    <select className={inputCls} value={form.occasion} onChange={set("occasion")}>
                      {OCCASIONS.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
                    </select>
                  </div>
                </>
              )}
              <div className="sm:col-span-2">
                <label className={labelCls} style={{ color: NAVY }}>Description</label>
                <textarea rows={3} className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-[13px] font-semibold outline-none focus:border-[#35C3C1] resize-none" value={form.description} onChange={set("description")} placeholder="Product description shown on the detail page" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} style={{ color: NAVY }}>How to Play (one step per line)</label>
                <textarea rows={3} className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-[13px] font-semibold outline-none focus:border-[#35C3C1] resize-none" value={form.howToPlay} onChange={set("howToPlay")} placeholder={"Choose a Mystery Character\nAsk Yes/No Questions..."} />
              </div>
              <div className="sm:col-span-2 flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.topPick} onChange={(e) => setForm((f) => ({ ...f, topPick: e.target.checked }))} className="h-4 w-4 accent-[#1B1F5C]" />
                  <span className="text-[12.5px] font-extrabold" style={{ color: NAVY }}>Show in Top Picks (home)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.newArrival} onChange={(e) => setForm((f) => ({ ...f, newArrival: e.target.checked }))} className="h-4 w-4 accent-[#1B1F5C]" />
                  <span className="text-[12.5px] font-extrabold" style={{ color: NAVY }}>Show in New Arrivals (home)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.inStock} onChange={(e) => setForm((f) => ({ ...f, inStock: e.target.checked }))} className="h-4 w-4 accent-[#1B1F5C]" />
                  <span className="text-[12.5px] font-extrabold" style={{ color: form.inStock ? "#4CAF50" : "#F0426A" }}>
                    {form.inStock ? "In Stock" : "Out of Stock"}
                  </span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={saving || uploading}
              className="mt-6 w-full h-12 rounded-full text-white text-[13px] font-extrabold tracking-widest shadow-md hover:shadow-lg transition-colors disabled:opacity-60"
              style={{ backgroundColor: NAVY }}
            >
              {saving ? "SAVING..." : editingId ? "SAVE CHANGES" : "ADD PRODUCT"}
            </button>
          </form>

          {/* Product list */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-7">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-2.5">
                <span className="h-10 w-10 rounded-full bg-[#E7F8F8] flex items-center justify-center">
                  <Package size={18} className="text-[#35C3C1]" />
                </span>
                <h2 className="font-display font-extrabold text-lg" style={{ color: NAVY }}>
                  Products <span className="text-gray-400 text-[14px]">({products.length})</span>
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <select className="h-9 px-3 rounded-lg border border-gray-200 text-[12px] font-extrabold outline-none" style={{ color: NAVY }} value={filterCat} onChange={(e) => setFilterCat(e.target.value)}>
                  <option value="">All Categories</option>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <button onClick={load} className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center hover:border-[#35C3C1] transition-colors" style={{ color: NAVY }} aria-label="Refresh">
                  <RefreshCw size={14} />
                </button>
              </div>
            </div>

            {loading ? (
              <div className="space-y-3">
                {[...Array(6)].map((_, i) => <div key={i} className="h-16 rounded-xl bg-gray-100 animate-pulse" />)}
              </div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[720px] overflow-y-auto pr-1">
                {products.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 py-3">
                    <div className="h-14 w-14 rounded-xl bg-[#F7F7FA] shrink-0 overflow-hidden flex items-center justify-center">
                      {p.image ? (
                        <img src={resolveImageUrl(p.image)} alt={p.name} className="h-full w-full object-contain mix-blend-multiply" />
                      ) : (
                        <Package size={20} className="text-gray-300" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-[13px] font-extrabold truncate" style={{ color: NAVY }}>{p.name}</p>
                      <p className="text-[11.5px] font-semibold text-gray-400">
                        {CATEGORIES.find((c) => c.value === p.category)?.label} · ₹{p.price.toLocaleString("en-IN")}
                        {p.off ? ` · ${p.off}` : ""}
                        {p.topPick ? " · Top Pick" : ""}
                        {p.newArrival ? " · New Arrival" : ""}
                      </p>
                    </div>
                    <button
                      onClick={() => toggleStock(p)}
                      className={`hidden md:inline-flex px-2.5 h-7 rounded-full text-[10px] font-extrabold tracking-wide transition-colors ${
                        p.inStock === false
                          ? "bg-[#FDE7EF] text-[#F0426A] hover:bg-[#F0426A] hover:text-white"
                          : "bg-[#E5F6E7] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white"
                      }`}
                      title="Click to toggle stock"
                    >
                      {p.inStock === false ? "OUT OF STOCK" : "IN STOCK"}
                    </button>
                    <span className="hidden sm:flex items-center gap-1 text-[11.5px] font-bold text-gray-400">
                      <Star size={11} className="text-[#F79433] fill-[#F79433]" /> {p.rating}
                    </span>
                    <button onClick={() => startEdit(p)} className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#35C3C1] hover:border-[#35C3C1] transition-colors" aria-label={`Edit ${p.name}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => handleDelete(p)} className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#F0426A] hover:border-[#F0426A] transition-colors" aria-label={`Delete ${p.name}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {products.length === 0 && (
                  <p className="py-10 text-center text-[13px] font-semibold text-gray-400">No products in this category yet.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Customer Enquiries */}
        <div className="mt-6 bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-7" data-testid="admin-enquiries-panel">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2.5">
              <span className="h-10 w-10 rounded-full bg-[#FBF3E4] flex items-center justify-center">
                <MessageSquare size={18} className="text-[#F79433]" />
              </span>
              <h2 className="font-display font-extrabold text-lg" style={{ color: NAVY }}>
                Customer Enquiries <span className="text-gray-400 text-[14px]">({filteredEnquiries.length})</span>
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {selectedEnq.length > 0 && (
                <button
                  data-testid="enquiry-bulk-delete-btn"
                  onClick={handleBulkDeleteEnquiries}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#F0426A] text-white text-[11.5px] font-extrabold hover:bg-[#d93058] transition-colors"
                >
                  <Trash2 size={13} /> DELETE ({selectedEnq.length})
                </button>
              )}
              <button
                data-testid="enquiry-export-view-btn"
                onClick={() => exportEnquiriesCSV(filteredEnquiries, enqFilter || "all")}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-gray-200 bg-white text-[11.5px] font-extrabold hover:border-[#35C3C1] hover:text-[#35C3C1] transition-colors"
                style={{ color: NAVY }}
                title="Export the enquiries currently shown (respects filter)"
              >
                <Download size={13} /> EXPORT VIEW
              </button>
              <button
                data-testid="enquiry-export-all-btn"
                onClick={() => exportEnquiriesCSV(enquiries, "all")}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-gray-200 bg-white text-[11.5px] font-extrabold hover:border-[#35C3C1] hover:text-[#35C3C1] transition-colors"
                style={{ color: NAVY }}
                title="Export every enquiry regardless of filter"
              >
                <Download size={13} /> EXPORT ALL
              </button>
              <select
                data-testid="enquiry-type-filter"
                className="h-9 px-3 rounded-lg border border-gray-200 text-[12px] font-extrabold outline-none"
                style={{ color: NAVY }}
                value={enqFilter}
                onChange={(e) => setEnqFilter(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="general">General</option>
                <option value="franchise">Franchise</option>
                <option value="newsletter">Newsletter</option>
              </select>
            </div>
          </div>
          {filteredEnquiries.length > 0 && (
            <label className="flex items-center gap-2 mb-2 cursor-pointer select-none w-fit" data-testid="enquiry-select-all">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[#1B1F5C]"
                checked={filteredEnquiries.length > 0 && filteredEnquiries.every((q) => selectedEnq.includes(q.id))}
                onChange={(e) => {
                  const ids = filteredEnquiries.map((q) => q.id);
                  setSelectedEnq((prev) => (e.target.checked ? [...new Set([...prev, ...ids])] : prev.filter((id) => !ids.includes(id))));
                }}
              />
              <span className="text-[11.5px] font-extrabold text-gray-500">Select all shown</span>
            </label>
          )}
          {filteredEnquiries.length === 0 ? (
            <p className="py-8 text-center text-[13px] font-semibold text-gray-400">No enquiries yet.</p>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[480px] overflow-y-auto text-left pr-1">
              {filteredEnquiries.map((q) => (
                <div key={q.id} className="py-3.5 flex items-start gap-3" data-testid={`enquiry-row-${q.id}`}>
                  <input
                    type="checkbox"
                    data-testid={`enquiry-checkbox-${q.id}`}
                    className="h-4 w-4 mt-1 accent-[#1B1F5C] shrink-0 cursor-pointer"
                    checked={selectedEnq.includes(q.id)}
                    onChange={() => toggleEnqSelect(q.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-[13.5px] font-extrabold flex items-center gap-2" style={{ color: NAVY }}>
                        {q.name}
                        <span
                          className={`px-2 h-5 rounded-full text-[9.5px] font-extrabold tracking-wide inline-flex items-center ${
                            q.type === "franchise" ? "bg-[#FDEEDC] text-[#F79433]" : q.type === "newsletter" ? "bg-[#EFE7FB] text-[#8B5CF6]" : "bg-[#E7F8F8] text-[#35C3C1]"
                          }`}
                        >
                          {q.type === "franchise" ? "FRANCHISE" : q.type === "newsletter" ? "NEWSLETTER" : "GENERAL"}
                        </span>
                      </p>
                      <p className="text-[11px] font-semibold text-gray-400">{new Date(q.createdAt).toLocaleString("en-IN")}</p>
                    </div>
                    <p className="text-[12px] font-bold text-[#35C3C1]">{q.phone}{q.email ? ` · ${q.email}` : ""}{q.city ? ` · ${q.city}` : ""}</p>
                    <p className="mt-1 text-[12.5px] font-semibold text-gray-500 leading-relaxed">{q.message}</p>
                  </div>
                  <button
                    data-testid={`enquiry-delete-btn-${q.id}`}
                    onClick={() => handleDeleteEnquiry(q)}
                    className="h-8 w-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#F0426A] hover:border-[#F0426A] transition-colors shrink-0"
                    aria-label={`Delete enquiry from ${q.name}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Admin;
